import { zodResolver } from "@hookform/resolvers/zod";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
  styled,
} from "@mui/material";
import { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";

import {
  UpdateSubscriberSchema,
  UpdateSubscriberSchemaType,
} from "@stw/yup-validators";

import {
  AudienceContactUpdateSource,
  GetAudienceContactFieldsQuery,
  UpdateAudienceContactMutation,
  UpdateAudienceContactMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { RhfErrorBanner } from "~frontend/components/forms/react-hook-form-components";
import ChevronDown from "~frontend/components/icons/ChevronDown";
import {
  GET_AUDIENCE_SUMMARY,
  UPDATE_AUDIENCE_CONTACT,
} from "~frontend/graphql/audience";
import { AudienceContact } from "~frontend/routes/subaccounts/audiences/Subscribers/types";
import { createUpdateSubscriberInitialValues } from "~frontend/routes/subaccounts/audiences/Subscribers/utils";
import theme, { palette } from "~frontend/theme";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import useMutation from "~frontend/util/useMutation";

import { ContactFieldsEditor } from "./ContactFieldsEditor";

const AudienceContactDialogActions = styled(DialogActions)({
  justifyContent: "flex-end",
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  marginBottom: theme.spacing(1),
});

type ContactHistoryRecord = { id: string; label: string; timestamp: number };

function AudienceContactDetails({
  audienceContact,
  editing = false,
  control,
  fieldDefinitions,
  showSaveSuccessful,
}: {
  audienceContact: AudienceContact;
  editing?: boolean;
  control: any;
  fieldDefinitions: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  showSaveSuccessful: boolean;
}) {
  const contactHistory: ContactHistoryRecord[] = audienceContact.updates.reduce(
    (acc, acu) => {
      let label = "Updated";

      switch (acu.source) {
        case AudienceContactUpdateSource.BulkUpload: {
          label =
            acu.sourceDetails.uploadFileName && acu.sourceDetails.uploadedBy
              ? `Bulk upload from ${acu.sourceDetails.uploadFileName} by ${acu.sourceDetails.uploadedBy}`
              : "Bulk upload";
          break;
        }
        case AudienceContactUpdateSource.AdminManual: {
          label = acu.sourceDetails.updatedBy
            ? `Details updated by ${acu.sourceDetails.updatedBy}`
            : "Details manually updated";
          break;
        }
        case AudienceContactUpdateSource.Api: {
          label = acu.sourceDetails.apiKeyName
            ? `Updated by API Key ${acu.sourceDetails.apiKeyName}`
            : "Updated by API";
          break;
        }

        case AudienceContactUpdateSource.Actblue: {
          label = "ActBlue donation";
          break;
        }

        case AudienceContactUpdateSource.ActionKit: {
          label = acu.sourceDetails.actionKitPageName
            ? `ActionKit form submission from ${acu.sourceDetails.actionKitPageName}`
            : "ActionKit form submission";
          break;
        }

        case AudienceContactUpdateSource.Keyword: {
          label = acu.sourceDetails.keyword
            ? `Sent keyword ${acu.sourceDetails.keyword}`
            : "Sent keyword";
          break;
        }

        case AudienceContactUpdateSource.LegacyShortCodeSubscriber: {
          label = "Bulk uploaded";
          break;
        }
      }

      acc.push({
        label,
        timestamp: acu.createdAt,
        id: acu.id,
      });
      return acc;
    },
    [] as ContactHistoryRecord[]
  );

  return (
    <FlexBox direction="column" gap={3} data-testid="audience-contact-details">
      <ContactFieldsEditor
        fields={audienceContact.fields}
        editing={editing}
        control={control}
        fieldDefinitions={fieldDefinitions}
        showSaveSuccessful={showSaveSuccessful}
      />

      <Accordion disableGutters sx={{ padding: 0, background: "none" }}>
        <AccordionSummary
          expandIcon={<ChevronDown sx={{ fontSize: "1em" }} />}
          sx={{
            backgroundColor: palette.midGray,
            minHeight: "27px",
            ".MuiAccordionSummary-content": { margin: 0 },
          }}
        >
          <Typography variant="body2Bold">Contact History</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            p: 0,
          }}
        >
          <List sx={{ backgroundColor: palette.lightestGray, px: 2 }}>
            {contactHistory.map(ch => (
              <ListItem
                sx={{
                  px: 0,
                }}
                key={`contact-history-${ch.id}`}
              >
                <ListItemText
                  primary={<Typography variant="body2">{ch.label}</Typography>}
                  secondary={
                    <Typography variant="body3">
                      {formatShortDateTime(ch.timestamp)}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </FlexBox>
  );
}

export function ArchivedAudienceContactDetails({
  audienceContact,
  audienceContactFields,
  onClose,
}: {
  audienceContact: AudienceContact;
  audienceContactFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  onClose: () => void;
}): ReactElement {
  const initialValues = createUpdateSubscriberInitialValues(
    audienceContact.id,
    audienceContact.fields,
    audienceContactFields
  );

  const methods = useForm<UpdateSubscriberSchemaType>({
    resolver: zodResolver(UpdateSubscriberSchema(audienceContactFields)),
    values: initialValues,
  });

  const { control } = methods;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testId="edit-audience-contact-details-dialog"
    >
      <DialogTitle sx={{ px: 5 }}>
        Contact Details for{" "}
        <strong>{prettyPhoneNumber(audienceContact.phoneNumber)}</strong>
      </DialogTitle>
      <DialogContent sx={{ px: 5 }}>
        <AudienceContactDetails
          audienceContact={audienceContact}
          editing={false}
          control={control}
          fieldDefinitions={audienceContactFields}
          showSaveSuccessful={false}
        />
      </DialogContent>
      <AudienceContactDialogActions sx={{ px: 5 }}>
        <Button onClick={onClose} variant="contained" color="tertiary">
          Close
        </Button>
      </AudienceContactDialogActions>
    </Dialog>
  );
}

export function AudienceContactDetailsDialog({
  audienceContact,
  updateAudienceContact,
  audienceId,
  audienceContactFields,
  onClose,
}: {
  audienceContact: AudienceContact;
  updateAudienceContact: () => void;
  audienceId: string;
  audienceContactFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  onClose: () => void;
}): ReactElement {
  const formatErrorMessage = useErrorMessageFormatter();
  const [mutationLoading, setMutationLoading] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [showSaveSuccessful, setShowSaveSuccessful] = useState(false);

  const [editing, setEditing] = useState(false);

  const initialValues = createUpdateSubscriberInitialValues(
    audienceContact.id,
    audienceContact.fields,
    audienceContactFields
  );

  const methods = useForm<UpdateSubscriberSchemaType>({
    resolver: zodResolver(UpdateSubscriberSchema(audienceContactFields)),
    mode: "onBlur",
    reValidateMode: "onBlur",
    values: initialValues,
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [updateAudienceContactMutation] = useMutation<
    UpdateAudienceContactMutation,
    UpdateAudienceContactMutationVariables
  >(UPDATE_AUDIENCE_CONTACT, {
    refetchQueries: [GET_AUDIENCE_SUMMARY],
    awaitRefetchQueries: true,
  });

  const handleUpdateSubmit = async (rawValues: unknown) => {
    const values = UpdateSubscriberSchema(audienceContactFields).parse(
      rawValues
    );
    setMutationLoading(true);
    setMutationError(null);

    const { errors } = await updateAudienceContactMutation({
      variables: {
        input: {
          audienceId,
          audienceContactId: audienceContact.id,
          fields: JSON.stringify(values.fields),
        },
      },
    });

    if (errors) {
      setMutationError(formatErrorMessage(errors));
    } else {
      updateAudienceContact();
      setEditing(false);
      setShowSaveSuccessful(true);
    }

    setMutationLoading(false);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testId="edit-audience-contact-details-dialog"
    >
      <DialogTitle sx={{ px: 5 }}>
        Contact Details for{" "}
        <strong>{prettyPhoneNumber(audienceContact.phoneNumber)}</strong>
      </DialogTitle>
      <DialogContent sx={{ px: 5 }}>
        {editing && <RhfErrorBanner control={control} />}
        {mutationError && <Alert severity="error">{mutationError}</Alert>}
        <AudienceContactDetails
          audienceContact={audienceContact}
          editing={editing}
          control={control}
          fieldDefinitions={audienceContactFields}
          showSaveSuccessful={showSaveSuccessful}
        />
      </DialogContent>
      <AudienceContactDialogActions sx={{ px: 5 }}>
        <Button onClick={onClose} variant="contained" color="tertiary">
          Close
        </Button>
        {editing ? (
          <Button
            onClick={handleSubmit(handleUpdateSubmit)}
            color="primary"
            variant="contained"
            disabled={isSubmitting || mutationLoading}
          >
            {isSubmitting || mutationLoading ? "Saving..." : "Save"}
          </Button>
        ) : (
          <Button
            onClick={() => {
              setShowSaveSuccessful(false);
              setEditing(true);
            }}
            color="primary"
            variant="contained"
          >
            Edit
          </Button>
        )}
      </AudienceContactDialogActions>
    </Dialog>
  );
}
